<template>
  <div class="customer-history">
    <div v-if="!customerPhone && !customerName" class="text-center p-5">
      <p class="text-muted">
        Khách hàng chưa có số điện thoại hoặc tên để tra cứu đơn hàng.
      </p>
    </div>

    <template v-else>
      <!-- Date & Type Filters -->
      <b-row class="mb-4 align-items-end">
        <b-col md="3" sm="5">
          <label class="small text-muted font-weight-bold">Từ ngày</label>
          <date-picker
            placeholder="Chọn ngày bắt đầu"
            class="form-control form-control-sm"
            :config="datePickerConfig"
            v-model="filters.fromDate"
          ></date-picker>
        </b-col>
        <b-col md="3" sm="5">
          <label class="small text-muted font-weight-bold">Đến ngày</label>
          <date-picker
            placeholder="Chọn ngày kết thúc"
            class="form-control form-control-sm"
            :config="datePickerConfig"
            v-model="filters.toDate"
          ></date-picker>
        </b-col>
        <b-col md="3" sm="5">
          <label class="small text-muted font-weight-bold">Loại đơn hàng</label>
          <b-form-select
            v-model="filters.tabName"
            :options="orderTypeOptions"
            size="sm"
            class="form-control form-control-sm"
          ></b-form-select>
        </b-col>
        <b-col md="3" sm="7">
          <b-button
            variant="primary"
            size="sm"
            @click="onApplyFilter"
            class="font-weight-bolder"
          >
            <i class="fas fa-search mr-1"></i> Lọc
          </b-button>
          <b-button
            variant="secondary"
            size="sm"
            @click="onResetFilter"
            class="ml-2"
          >
            Làm mới
          </b-button>
        </b-col>
      </b-row>

      <div class="list-bill">
        <table class="table table-bordered-list table-hover col-md-12">
          <thead>
            <tr>
              <th class="text-center tdCode">ID</th>
              <th class="text-center tdProductName">Sản phẩm</th>
              <th class="text-center tdProductPrice">Giá</th>
              <th class="text-center tdQuantity">SL</th>
              <th class="text-center tdTotalAmount">Tổng tiền</th>
              <th class="text-center tdTotalPayment">Thanh toán</th>
              <th class="text-center tdNote">Ghi chú</th>
              <th class="text-center tdStatus">Trạng thái</th>
            </tr>
          </thead>
          <tbody v-if="!isLoading && items.length > 0">
            <template v-for="(order, i) in items">
              <!-- Header Row of each Order -->
              <tr
                :key="'h-' + order.id + '-' + i"
                @mouseenter="hoveredOrderId = order.id"
                @mouseleave="hoveredOrderId = null"
                :class="{ 'tr-rowspan-hover': hoveredOrderId === order.id }"
                :data="`row_${order.id}`"
              >
                <td
                  colspan="6"
                  style="
                    padding: 5px !important;
                    border-bottom: none !important;
                  "
                >
                  <div class="d-flex align-items-start" style="font-size: 0.87rem">
                    <span class="mr-4" v-if="order.orderType">
                      <div class="text-center d-inline-block">
                        <i
                          v-if="order.orderType === ENUM.TYPE_ORDER.SHOP"
                          class="fas fa-store icon-color text-warning mr-1"
                          v-b-tooltip.hover.bottom
                          title="Mua tại quầy"
                        ></i>
                        <i
                          v-else-if="order.orderType === ENUM.TYPE_ORDER.PREORDER"
                          class="fas fa-shopping-basket icon-color text-danger mr-1"
                          v-b-tooltip.hover.bottom
                          title="Đặt trước"
                        ></i>
                        <i
                          v-else-if="order.orderType === ENUM.TYPE_ORDER.SHIPPING"
                          class="fas fa-truck icon-color text-success mr-1"
                          v-b-tooltip.hover.bottom
                          title="Tự vận chuyển"
                        ></i>
                        <i
                          v-else-if="order.orderType === ENUM.TYPE_ORDER.EX_SHIPPING"
                          class="fas fa-box icon-color text-primary mr-1"
                          v-b-tooltip.hover.bottom
                          title="Hãng vận chuyển (Ahamove, Nhất tín ...)"
                        ></i>
                        <i
                          v-else-if="order.orderType === ENUM.TYPE_ORDER.WEBSITE"
                          class="fas fa-globe-asia text-dark mr-1"
                          v-b-tooltip.hover.bottom
                          title="Website"
                        ></i>
                        <i
                          v-else-if="order.orderType === ENUM.TYPE_ORDER.WHOLESALE"
                          class="fas fa-truck-loading mr-1"
                          v-b-tooltip.hover.bottom
                          title="Bán buôn"
                        ></i>
                      </div>
                    </span>
                    <span class="mr-4">
                      Ngày tạo:
                      <span class="text-primary">{{ formatDate(order.createdAt, 'DD/MM/YYYY HH:mm') }}</span>
                    </span>
                    <span class="mr-4" v-if="order.createdAt !== order.updatedAt">
                      Cập nhật:
                      <span class="text-primary">{{ formatDate(order.updatedAt, 'DD/MM/YYYY HH:mm') }}</span>
                    </span>
                    <span v-if="order.orderSourceName" class="mr-4">
                      <span class="mr-1 text-muted">Nguồn:</span>
                      <span>{{ order.orderSourceName }}</span>
                    </span>
                    <span class="mr-4" v-if="order.createdByName">
                      <span class="mr-1 text-muted">NVCR:</span>
                      <span>
                        {{ order.createdByName }}
                        <small v-if="order.departmentName">({{ order.departmentName }})</small>
                      </span>
                    </span>
                    <span class="mr-4" v-if="order.saleStoreName">
                      <span class="mr-1 text-muted">NVBH:</span>
                      <span>{{ order.saleStoreName }}</span>
                    </span>
                  </div>
                </td>
                <td style="border-bottom: none !important" colspan="2">
                  <div class="d-flex justify-content-between align-items-center" style="font-size: 0.87rem">
                    <span v-if="order.partnerPaymentStatus">
                      <span class="mr-1 text-muted">Thanh toán:</span>
                      <span :class="bindClassNamePayment(order)">{{ order.partnerPaymentStatusName }}</span>
                    </span>
                    <i class="fa fa-arrow-circle-o-down" aria-hidden="true"></i>
                  </div>
                </td>
              </tr>

              <!-- Items Rows of each Order -->
              <template v-for="(orderItem, j) in order.orderItems">
                <tr
                  v-if="j < limitListProduct"
                  :key="'p-' + order.id + '-' + orderItem.orderItemId + '-' + j"
                  @mouseenter="hoveredOrderId = order.id"
                  @mouseleave="hoveredOrderId = null"
                  :class="{ 'tr-rowspan-hover': hoveredOrderId === order.id }"
                  :data="`row_${order.id}`"
                >
                  <td
                    :rowspan="
                      order.orderItems.length > limitListProduct
                        ? limitListProduct
                        : order.orderItems.length
                    "
                    v-if="j === 0 || order.show"
                    class="tdTextAlign tdCode"
                  >
                    <div class="orderCode" style="cursor: pointer; color: #3699ff">
                      <a class="font-weight-bold" :href="getOrderLink(order.id)" target="_self">
                        {{ order.id }}
                      </a>
                    </div>
                  </td>

                  <td class="tdTextAlign tdProductName">
                    <div class="d-flex align-items-center flex-gap-8">
                      <!-- Product Image -->
                      <ProductImage
                        :src="orderItem.imageUrl"
                        class="size-40 object-contain flex-shrink-0"
                      />
                      <!-- Product Information -->
                      <div class="flex-grow-1 min-w-0">
                        <p class="m-0 text-11 lh-1-4 product-codes">
                          <i
                            class="fas fa-gift icon-color gift"
                            v-if="orderItem.orderItemType === ENUM.ITEM_TYPE.GIFT"
                            title="Quà tặng"
                          ></i>
                          <i
                            class="fas fa-tags icon-color attach"
                            v-else-if="orderItem.orderItemType === ENUM.ITEM_TYPE.ATTACH"
                            title="Bán kèm"
                          ></i>
                          {{ orderItem.productBarCode }}
                        </p>
                        <p class="m-0 text-12 lh-1-4">({{ orderItem.productCode }})</p>
                        <p class="m-0 text-medium lh-1-4 product-name">{{ orderItem.productName }}</p>
                      </div>
                    </div>
                    <p
                      v-if="orderItem.imeiCode && orderItem.imeiCode.trim().length > 0"
                      class="m-0 product-meta"
                    >
                      <i class="text-primary text-12 font-italic">IMEI: {{ orderItem.imeiCode }}</i>
                    </p>
                  </td>

                  <td class="tdTextAlignPrice tdProductPrice">
                    {{ convertPrice(orderItem.productPrice) }}
                  </td>
                  <td class="tdTextAlignPrice tdQuantity">
                    {{ orderItem.quantity }}
                  </td>

                  <td
                    class="tdTextAlignPrice tdTotalAmount"
                    :rowspan="
                      order.orderItems.length > limitListProduct
                        ? limitListProduct
                        : order.orderItems.length
                    "
                    v-if="j === 0 || order.show"
                  >
                    <b style="color: #3c763d">{{ convertPrice(order.totalAmount) }}</b>
                  </td>

                  <td
                    class="tdTextAlignPrice tdTotalPayment"
                    :rowspan="
                      order.orderItems.length > limitListProduct
                        ? limitListProduct
                        : order.orderItems.length
                    "
                    v-if="j === 0 || order.show"
                  >
                    <template v-if="order.payments && order.payments.length">
                      <div v-for="(payment, pIdx) in order.payments" :key="pIdx" class="mb-2">
                        <span class="mr-2">
                          <i
                            v-b-tooltip.hover
                            :title="payment.paymentName"
                            class="icon-color text-dark mr-1"
                            :class="
                              payment.paymentType === 1
                                ? 'fas fa-money-bill-alt'
                                : payment.paymentType === 2
                                  ? 'fa fa-university'
                                  : payment.paymentType === 3
                                    ? 'fa fa-credit-card'
                                    : payment.paymentType === 4
                                      ? 'fa fa-cube'
                                      : ''
                            "
                          ></i>:
                        </span>
                        <span style="font-weight: 700">{{ convertPrice(payment.paymentAmount) }}</span>
                      </div>
                    </template>
                    <p v-if="order.extraAmount > 0" class="d-block text-primary">
                      Tiền thừa: <span class="d-block">{{ convertPrice(order.extraAmount) }}</span>
                    </p>
                    <p v-if="order.extraAmount < 0" class="d-block" style="color: #e65b5b">
                      Còn thiếu: <span class="d-block">{{ convertPrice(order.extraAmount * -1) }}</span>
                    </p>
                    <p v-show="order.leftAmount > 0">
                      Công nợ: <span class="d-block">{{ convertPrice(order.leftAmount) }}</span>
                    </p>
                  </td>

                  <td
                    class="tdTextAlign tdNote wrapword"
                    :rowspan="
                      order.orderItems.length > limitListProduct
                        ? limitListProduct
                        : order.orderItems.length
                    "
                    v-if="j === 0 || order.show"
                  >
                    <p v-if="order.customerNote" :title="order.customerNote" v-b-tooltip.hover>
                      {{ truncateText(order.customerNote) }}
                    </p>
                    <p v-if="order.customerNote && (order.saleNote || order.warrantyNote)" class="dashed"></p>
                    <p v-if="order.saleNote" :title="order.saleNote" v-b-tooltip.hover>
                      {{ truncateText(order.saleNote) }}
                    </p>
                    <p v-if="order.saleNote && order.warrantyNote" class="dashed"></p>
                    <p v-if="order.warrantyNote" :title="order.warrantyNote" v-b-tooltip.hover>
                      {{ truncateText(order.warrantyNote) }}
                    </p>
                  </td>

                  <td
                    class="tdTextAlign tdStatus text-center"
                    :rowspan="
                      order.orderItems.length > limitListProduct
                        ? limitListProduct
                        : order.orderItems.length
                    "
                    v-if="j === 0 || order.show"
                  >
                    <span :class="getStatusBadgeClass(order)">
                      {{ order.statusName }}
                    </span>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
          <tbody v-else-if="isLoading">
            <tr>
              <td colspan="8" class="text-center p-5">
                <div class="text-center text-primary my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong class="ml-2">Đang tải...</strong>
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="8" class="text-center text-muted p-5">
                Không có đơn hàng nào
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-3">
        <p class="text-muted mb-0">
          Tổng số: <strong>{{ totalItems }}</strong> đơn hàng
        </p>
        <BPaginationNavCustom
          v-if="totalPages > 1"
          :number-of-pages="totalPages || 1"
          :current-page="Number(currentPage)"
          :total-page="totalPages"
          :link-params="{ id: customerId }"
          @page-change="onPageChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref } from 'vue';
import ApiService from '@/core/services/api.service';
import { convertPrice, truncateText, formatDate } from '@/utils/common';
import ProductImage from '@/view/components/products/ProductImage.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import moment from 'moment';
import { getOrderStatusBadgeClass } from '@/utils/enum-name';
import useCustomerHistory from '../composables/useCustomerHistory';

export default defineComponent({
  name: 'CustomerOrderHistoryTab',

  components: {
    ProductImage,
    BPaginationNavCustom,
    datePicker,
  },

  props: {
    customerId: {
      type: [String, Number],
      required: true,
    },
    customerPhone: {
      type: String,
      default: '',
    },
    customerName: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const limitListProduct = 10;
    const hoveredOrderId = ref(null);

    const ENUM = {
      ITEM_TYPE: {
        MAIN: 1,
        GIFT: 2,
        ATTACH: 3,
      },
      TYPE_ORDER: {
        SHOP: 1,
        PREORDER: 2,
        SHIPPING: 3,
        EX_SHIPPING: 4,
        WEBSITE: 5,
        WHOLESALE: 6,
      },
      PARTNER_PAYMENT: {
        SUCCESS: 1,
        FAIL: 2,
        AGREE_DEPOSIT: 3,
        DEPOSIT: 4,
      },
    };

    const filters = ref({
      fromDate: null,
      toDate: null,
      tabName: null,
    });

    const datePickerConfig = {
      format: 'DD/MM/YYYY',
      useCurrent: false,
      showClear: true,
      showClose: true,
      locale: 'vi',
    };

    const orderTypeOptions = [
      { value: null, text: 'Tất cả' },
      { value: 'pos', text: 'Đơn hàng' },
      { value: 'shopee', text: 'Đơn sàn' },
    ];

    const getOrderLink = (orderId) => {
      return `#/orders/edit-order/${orderId}`;
    };

    const bindClassNamePayment = (order) => {
      switch (order.partnerPaymentStatus) {
        case ENUM.PARTNER_PAYMENT.SUCCESS:
          return 'text-success';
        case ENUM.PARTNER_PAYMENT.FAIL:
          return 'text-danger';
        case ENUM.PARTNER_PAYMENT.AGREE_DEPOSIT:
          return 'text-primary';
        case ENUM.PARTNER_PAYMENT.DEPOSIT:
          return 'text-warning';
        default:
          return '';
      }
    };

    const getStatusBadgeClass = (order) => {
      return getOrderStatusBadgeClass(order.status);
    };

    const loadOrders = async (page) => {
      const params = {
        page,
        limit: 10,
        customerId: Number(props.customerId),
      };

      if (filters.value.fromDate) {
        params.searchFromDay = moment(filters.value.fromDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      }
      if (filters.value.toDate) {
        params.searchToDay = moment(filters.value.toDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      }
      if (filters.value.tabName) {
        params.tabName = filters.value.tabName;
      }

      const response = await ApiService.query('v1/orders', { params });
      const data = response.data.data || {};
      const dataset = data.dataset || [];

      const mappedItems = dataset.map((item) => {
        const orderItems = item.orderItems || [];
        return {
          ...item,
          show: orderItems.length <= 0,
          orderItems: orderItems,
        };
      });

      return {
        items: mappedItems,
        totalRow: data.total || 0,
        totalPage: data.pages || 0,
      };
    };

    const {
      items,
      totalItems,
      totalPages,
      currentPage,
      isLoading,
      fetchPage,
      onPageChange,
    } = useCustomerHistory(loadOrders);

    const onApplyFilter = () => {
      fetchPage(1);
    };

    const onResetFilter = () => {
      filters.value.fromDate = null;
      filters.value.toDate = null;
      filters.value.tabName = null;
      fetchPage(1);
    };

    onMounted(() => {
      if (props.customerPhone || props.customerName) {
        fetchPage(1);
      }
    });

    return {
      items,
      totalItems,
      totalPages,
      currentPage,
      isLoading,
      onPageChange,
      convertPrice,
      truncateText,
      formatDate,
      limitListProduct,
      hoveredOrderId,
      getOrderLink,
      ENUM,
      filters,
      datePickerConfig,
      orderTypeOptions,
      bindClassNamePayment,
      getStatusBadgeClass,
      onApplyFilter,
      onResetFilter,
    };
  },
});
</script>

<style scoped>
.customer-history {
  padding: 1rem 0;
}

.orderCode:hover {
  text-decoration: underline;
}

.table th,
.table td {
  vertical-align: middle;
}

.tdTextAlign {
  vertical-align: middle;
}

.tdTextAlignPrice {
  text-align: right !important;
  vertical-align: middle;
}

.table-bordered-list th {
  border: 0.5px solid #d2d4d6 !important;
  background-color: #e0e0e0;
}

.table-bordered-list td {
  border: 0.5px solid #f3f3f3;
  border-bottom: 0.5px solid #adadad;
}

.text-weight {
  font-weight: 550;
}

.tdCode {
  width: 5%;
}

.tdProductName {
  width: 38%;
}

.tdProductPrice {
  width: 8%;
}

.tdQuantity {
  width: 3%;
}

.tdTotalAmount {
  width: 8%;
}

.tdTotalPayment {
  width: 12%;
  font-weight: 700;
}

.tdNote {
  width: 14%;
}

.tdStatus {
  width: 8%;
}

.flex-gap-8 {
  gap: 8px;
}

.size-40 {
  width: 40px;
  height: 40px;
}
</style>

<style lang="scss" scoped>
.customer-history ::v-deep {
  .bill-header-meta {
    font-size: 0.87rem;
  }

  .customer-header-info {
    margin-bottom: 2px;
    font-size: 0.87rem;
    line-height: 1.35;

    > small.text-muted {
      margin-right: 4px;
    }
  }

  .dashed {
    border: 1px dashed #464e5f;
    border-style: none none dashed;
    color: #fff;
    background-color: #fff;
  }

  .product-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .product-codes {
    font-size: 12px;
    color: #475569;
    font-weight: 500;
  }

  .code-text {
    margin-right: 4px;
  }

  .product-title {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 14px;
    color: #111827;
  }

  .product-icon {
    margin-right: 6px;
    font-size: 14px;
  }

  .product-icon.gift {
    color: #2e7d32;
  }

  .product-icon.attach {
    color: #37474f;
  }

  .product-name {
    font-size: 14px;
  }

  .product-meta {
    margin-left: 2px;
    margin-top: 2px;
  }

  .tdNote {
    width: 14%;
    font-size: 0.8125rem;
  }
}
</style>
